import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';

import { UserReduxState } from 'legacy/user/types';
import { LoadingState, ErrorState } from 'legacy/reducers/types';

import cn from 'classnames';
import { Input } from 'antd';
import { updatePassword } from 'legacy/actions/userActions';

import { showModal } from 'legacy/actions/modalActions';

import { usePrevious } from '../../../../util/hooks';
import styles from './ProfileSettings.module.scss';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import DeleteAccountModal from './DeleteAccountModal';

interface LoginFields {
  oldPassword?: string;
  newPassword?: string;
}

interface LoginProps {
  user: any;
  errors: ErrorState;
  loading: LoadingState;
  updatePassword: (fields: LoginFields) => void;
  showModal: (type: string, config: any) => void;
}

const Login = ({
  user = {} as any,
  errors = {} as ErrorState,
  loading = {} as LoadingState,
  updatePassword,
  showModal
}: LoginProps) => {
  const { t } = useTranslation(['users']);
  const [passwordReset, setPasswordReset] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [fields, setFields] = useState<LoginFields>({});
  const [formErrors, setFormErrors] = useState<LoginFields>({});
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] =
    useState(false);

  useEffect(() => {
    setFormErrors((prev) => ({
      ...prev,
      oldPassword: !fields.oldPassword && t('users:settings.login.form.oldPasswordRequired'),
      newPassword: !fields.newPassword && t('users:settings.login.form.newPasswordRequired')
    }));
  }, [fields.oldPassword, fields.newPassword, t]);

  const handleFieldChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    setFields((fields) => ({
      ...fields,
      [name]: value
    }));
  };

  const prevEditLoading = usePrevious(loading.EDIT_ME);
  const prevPasswordLoading = usePrevious(loading.UPDATE_PASSWORD);

  const handleApiError = (
    loadingType: boolean | undefined,
    dispatchType: string,
    successMessage: string,
    onSuccess?: () => void
  ) => {
    if (!loading[dispatchType] && loadingType) {
      if (isEmpty(errors[dispatchType])) {
        showModal('ALERT', {
          message: successMessage,
          type: 'success'
        });

        onSuccess?.();
      } else {
        showModal('ALERT', { message: errors[dispatchType].message });
      }
    }
  };

  useEffect(() => {
    handleApiError(prevEditLoading, 'EDIT_ME', t('users:settings.login.success.profile'), () => {});
  }, [loading.EDIT_ME, errors.EDIT_ME, t]);

  useEffect(() => {
    handleApiError(
      prevPasswordLoading,
      'UPDATE_PASSWORD',
      t('users:settings.login.success.password'),
      () => {
        setFields({});
        setPasswordReset(false);
      }
    );
  }, [loading.UPDATE_PASSWORD, errors.UPDATE_PASSWORD, t]);

  const submitPassword = () => {
    setHasSubmitted(true);
    if (!formErrors.newPassword && !formErrors.oldPassword) {
      updatePassword(fields);
    }
  };

  return (
    <>
      <h1>{t('users:settings.login.title')}</h1>

      {user.isCustom && !passwordReset && (
        <Button
          onClick={() => setPasswordReset(true)}
          className="w-full md:!w-[20vw]"
          type={ButtonType.Primary}
        >
          {t('users:settings.login.changePassword')}
        </Button>
      )}
      <div className="flex gap-2 flex-col pb-2 mt-6">
        <p>{t('users:settings.login.deleteAccount.title')}</p>
        <div className="flex">
          <Button
            type={ButtonType.Danger}
            onClick={() => setIsDeleteAccountModalOpen(true)}
            className={styles['delete-button']}
          >
            {t('users:settings.login.deleteAccount.button')}
          </Button>
        </div>
      </div>
      {passwordReset && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            type="password"
            className={cn(styles.input, {
              [styles.hasError]: hasSubmitted && formErrors.oldPassword
            })}
            addonBefore={t('users:settings.login.form.oldPassword')}
            name="oldPassword"
            value={fields.oldPassword || ''}
            disabled={!passwordReset}
            onChange={handleFieldChange}
          />
          {hasSubmitted && formErrors.oldPassword && (
            <p className={styles.error}>{formErrors.oldPassword}</p>
          )}
          <Input
            type="password"
            className={cn(styles.input, {
              [styles.hasError]: hasSubmitted && formErrors.newPassword
            })}
            addonBefore={t('users:settings.login.form.newPassword')}
            name="newPassword"
            value={fields.newPassword || ''}
            onChange={handleFieldChange}
          />
          {formErrors.newPassword && hasSubmitted && (
            <p className={styles.error}>{formErrors.newPassword}</p>
          )}
          <Button onClick={submitPassword} loading={loading.UPDATE_PASSWORD}>
            {t('users:settings.login.form.confirm')}
          </Button>
        </div>
      )}

      {isDeleteAccountModalOpen && (
        <Modal
          type={ModalType.SMALL}
          onClose={() => setIsDeleteAccountModalOpen(false)}
          autoHeight
          hideCloseButton
          visible={isDeleteAccountModalOpen}
        >
          <DeleteAccountModal
            handleCancel={() => setIsDeleteAccountModalOpen(false)}
            onComplete={() => {
              setIsDeleteAccountModalOpen(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (state: { user: UserReduxState }) => ({
  fbLoaded: state.user.fbLoaded,
  loading: state.user.loading,
  errors: state.user.errors
});

const actions = { showModal, updatePassword };

export default connect(mapStateToProps, actions)(Login);
